import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { getTranslationValue } from "@customer-app/account/views/OperationAquarius/utils";
import { Box, Stack, Typography, styled } from "@mui/material";
import { typedLocalize, typedLocalizeKey } from "@smartrr/shared/utils/sharedTranslations/localize";
import * as Icon from "akar-icons";
import compact from 'lodash/compact';
import React, { useMemo } from "react";

import { renderSequenceNumber } from "../../../../OrderHistory/utils";
import { LoyaltyStoreAccess } from "../../../store";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { Trans } from "react-i18next";
import { useShowActiveSubscriberMultiplier } from "../../../utils/useShowActiveSubscriberMultiplier";

type EventList =
  | "ACCOUNT_CREATED"
  | "SUCCESSFUL_REFERRAL"
  | "BILLING_SUCCESS"
  | "REVIEW_CREATED"
  | "MANUAL"
  | "BONUS_EARN";

const CustomIcon = ({ Icon }: { Icon: Icon.Icon }) => {
  const theme = useSmartrrTheme();

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        padding: "7.5px",
        bgcolor: theme.modernBadgeBackground,
        borderRadius: "150px",
        width: "30px",
        height: "30px",
      }}
    >
      <Icon size={16} />
    </Box>
  );
};

const ActiveSubscriberInfo = styled("div")`
  background-color: ${props => props.theme.modernPageBackground};
  padding: 12px;
`;

interface EventInfo {
  descriptor: string;
  enabled: boolean;
  name: string;
  headline: string;
  action: string;
  Icon: Icon.Icon;
}

export interface HowToEarnPointsModalProps {
  title: string;
}

export const HowToEarnPointsModal = ({}: HowToEarnPointsModalProps) => {
  const events = LoyaltyStoreAccess.useEvents();
  const referral = LoyaltyStoreAccess.useReferralProgram();
  const description = LoyaltyStoreAccess.useDescription();
  const bonus = LoyaltyStoreAccess.useBonus();

  const activeSubscriberMultipler = LoyaltyStoreAccess.useActiveSubscriberMultiplier();
  const showMultiplier = useShowActiveSubscriberMultiplier();

  const referralPoints = referral?.pointsInReward ?? 0;

  const referralPointsSuffix = LoyaltyStoreAccess.useSuffix(referralPoints);
  const referralPointsText = LoyaltyStoreAccess.useText(referralPoints);
  const zeroPoints = LoyaltyStoreAccess.useText(0);
  const bonusEarnSuffix = LoyaltyStoreAccess.useSuffix(bonus?.givePoints ?? 0);

  const accountCreatedPoints = events?.find(ev => ev.type === "ACCOUNT_CREATED")?.pointsEarned ?? 0;
  const accountCreatedSuffix = LoyaltyStoreAccess.useSuffix(accountCreatedPoints);

  const billSuccessPoints = events?.find(ev => ev.type === "BILLING_SUCCESS")?.pointsEarned ?? 0;
  const billSuccessMultiplier =
    showMultiplier && activeSubscriberMultipler
      ? billSuccessPoints * activeSubscriberMultipler
      : billSuccessPoints;
  const billSuccessSuffix = LoyaltyStoreAccess.useSuffix(billSuccessMultiplier);

  const { symbol: currencySymbol } = LoyaltyStoreAccess.useCurrency();

  // unit name is always in plural
  const unitName = LoyaltyStoreAccess.useSuffix(2);

  const eventInfo: Record<EventList, EventInfo> = useMemo(() => {
    return {
      // this map must be in the render loop so typedLocalize can see the translations
      ACCOUNT_CREATED: {
        descriptor: "account",
        enabled: !!events?.find(ev => ev.type === "ACCOUNT_CREATED"),
        name: typedLocalize("loyalty.createAccount"),
        headline: typedLocalize("loyalty.displayPoints", {
          amount: accountCreatedPoints,
          unitName: accountCreatedSuffix,
          currency: currencySymbol,
        }),
        action: typedLocalize("loyalty.displayPoints", {
          amount: accountCreatedPoints,
          unitName: accountCreatedSuffix,
          currency: currencySymbol,
        }),
        Icon: Icon.Person,
      },
      SUCCESSFUL_REFERRAL: {
        descriptor: "referral",
        enabled: !!referral,
        name: typedLocalize("loyalty.referringAFriend"),
        headline: referralPointsSuffix,
        action: referralPointsText,
        Icon: Icon.Star,
      },
      BILLING_SUCCESS: {
        descriptor: "billing",
        enabled: !!events?.find(ev => ev.type === "BILLING_SUCCESS"),
        name: typedLocalize("loyalty.orders"),
        headline: typedLocalize("loyalty.spentEqualsPoints", {
          amount: billSuccessMultiplier,
          unitName: billSuccessSuffix,
          currency: currencySymbol,
        }),
        action: typedLocalize("loyalty.pointsPerSpent", {
          amount: billSuccessMultiplier,
          unitName: billSuccessSuffix,
          currency: currencySymbol,
        }),
        Icon: Icon.Cart,
      },
      REVIEW_CREATED: {
        descriptor: "reviews",
        enabled: false,
        name: typedLocalize("loyalty.notSupported"),
        headline: zeroPoints,
        action: zeroPoints,
        Icon: Icon.Star,
      },
      MANUAL: {
        descriptor: "manual",
        enabled: false,
        name: typedLocalize("loyalty.notSupported"),
        headline: zeroPoints,
        action: zeroPoints,
        Icon: Icon.Star,
      },
      BONUS_EARN: {
        descriptor: "bonus",
        enabled: !!bonus,
        name: typedLocalize("loyalty.bonusEarn"),
        headline: bonusEarnSuffix,
        action: typedLocalize("loyalty.pointOnEveryXOrder", {
          amount: bonus?.givePoints ?? 0,
          unitName: bonusEarnSuffix,
          xOrder: renderSequenceNumber(bonus?.every ?? 0),
        }),
        Icon: Icon.Sparkles,
      },
    };
  }, [events, referral, bonus]);

  const showComponent = compact([
    eventInfo.ACCOUNT_CREATED.enabled ? eventInfo.ACCOUNT_CREATED : null,
    eventInfo.BILLING_SUCCESS.enabled ? eventInfo.BILLING_SUCCESS : null,
    eventInfo.SUCCESSFUL_REFERRAL.enabled ? eventInfo.SUCCESSFUL_REFERRAL : null,
    eventInfo.BONUS_EARN.enabled ? eventInfo.BONUS_EARN : null,
  ]);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      py={showMultiplier ? "10px" : "30px"}
      spacing="20px"
      role="list"
    >
      {showMultiplier ? (
        <ActiveSubscriberInfo>
          <Typography variant="body1" align="center">
            <Trans
              i18nKey={typedLocalizeKey("loyalty.activeSubscriberMultiplier.earnings")}
              components={[<span key={0} />, <strong key={1} />]}
              values={{
                multiplier: activeSubscriberMultipler,
                unitName,
              }}
            />
          </Typography>
        </ActiveSubscriberInfo>
      ) : null}
      {eventInfo.BILLING_SUCCESS.enabled ? (
        <Typography variant="h4" className={prefixClassName("how-to-earn-billing-headline")}>
          {eventInfo.BILLING_SUCCESS.headline}
        </Typography>
      ) : null}
      <Typography
        variant="body1"
        sx={{ textAlign: "center" }}
        className={prefixClassName("how-to-earn-description")}
      >
        {getTranslationValue("loyalty.description", description)}
      </Typography>

      <Stack direction="column" spacing="15px">
        {showComponent.map((component, index) => (
          <Stack direction="row" alignItems="center" spacing="20px" sx={{ width: "100%" }} key={index}>
            <CustomIcon Icon={component.Icon} />
            <Stack direction="column" spacing="3px" role="listitem">
              <Typography
                variant="body1"
                role="term"
                className={prefixClassName(`how-to-earn-${component.descriptor}-name`)}
              >
                {component.name}
              </Typography>
              <Typography
                variant="caption1"
                sx={{ opacity: "70%" }}
                role="definition"
                className={prefixClassName(`how-to-earn-${component.descriptor}-action`)}
              >
                {component.action}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
